import { useState, useEffect, useCallback } from 'react';
import DataService from '../XXX/DataService';

// ─── Module-level cache (shared across all hook instances) ───────────
let nameCache: Record<string, string> = {};
let cacheLoaded = false;
let cachePromise: Promise<void> | null = null;

async function loadNameCache(): Promise<void> {
  if (cacheLoaded) return;
  if (cachePromise) return cachePromise;

  cachePromise = (async () => {
    try {
      const version = await DataService.fetchLatestVersion();
      const response = await fetch(
        `https://ddragon.leagueoflegends.com/cdn/${version}/data/en_US/champion.json`
      );
      const data = await response.json();

      for (const champData of Object.values(data.data) as any[]) {
        nameCache[champData.id] = champData.name;
      }
      // Special entries
      nameCache['DummyChampion'] = 'Practice Dummy';
      cacheLoaded = true;
    } catch (err) {
      console.error('Failed to load champion display names:', err);
    }
  })();

  return cachePromise;
}

/**
 * Synchronous lookup — returns the pretty display name for an internal
 * champion ID (e.g. "Kaisa" → "Kai'Sa", "MonkeyKing" → "Wukong").
 * Falls back to the raw ID if the cache hasn't loaded yet or the ID is unknown.
 */
export function getChampionDisplayName(internalId: string): string {
  return nameCache[internalId] || internalId;
}

/**
 * React hook that ensures the champion-name cache is loaded and provides
 * a stable `getDisplayName` function.
 *
 * Usage:
 *   const { getDisplayName } = useChampionDisplayNames();
 *   return <h1>{getDisplayName(championName)}</h1>;
 *
 * The underlying fetch only happens once no matter how many components
 * mount this hook.
 */
export function useChampionDisplayNames() {
  const [loaded, setLoaded] = useState(cacheLoaded);

  useEffect(() => {
    if (cacheLoaded) {
      setLoaded(true);
      return;
    }
    loadNameCache().then(() => setLoaded(true));
  }, []);

  const getDisplayName = useCallback(
    (internalId: string): string => nameCache[internalId] || internalId,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [loaded],
  );

  return { getDisplayName, loaded };
}
